import React, { useState } from 'react'
import { useParams } from 'react-router-dom'

import { FormattedMessage } from '@divvy-web/i18n'
import { TOAST_TYPE_DANGER, TOAST_TYPE_SUCCESS, useToast } from '@divvy-web/skylab.toast'

import { useApolloQueryErrorHandling } from '@sputnik/apollo/useApolloQueryErrorHandling'
import useGetCreditAppTypes from '@sputnik/hooks/useGetCreditAppTypes'
import AddOutsourcedAccountantDrawer from '@sputnik/pages/CreditApplicationPages/PeopleInfoForm/AddOutsourcedAccountantDrawer'
import { useGetPeopleInfoData } from '@sputnik/pages/CreditApplicationPages/PeopleInfoForm/GetPeopleInfoData.gql'
import PersonRecordWithActions from '@sputnik/pages/CreditApplicationPages/PeopleInfoForm/PersonRecordWithActions'
import PersonSection from '@sputnik/pages/CreditApplicationPages/PeopleInfoForm/PersonSection'
import UpdateOutsourcedAccountantDrawer from '@sputnik/pages/CreditApplicationPages/PeopleInfoForm/UpdateOutsourcedAccountantDrawer'
import { limitedAddPersonForm } from '@sputnik/schema/creditApplication/addPerson/addPersonSchema'
import { useWebTracking } from '@sputnik/tracking/WebTrackingContext'
import { logError, logInfo } from '@sputnik/utils/loggerUtils'

import { useRemoveOutsourcedAccountant } from './RemoveOutsourcedAccountant.gql'

const OutsourcedAccountantSection = () => {
  const { appId } = useParams()
  const showToast = useToast()
  const { isMajorAccountChange } = useGetCreditAppTypes({ salesforceCreditId: appId ?? '' })
  const [isShowingAddOutsourcedAccountantDrawer, setIsShowingAddOutsourcedAccountantDrawer] = useState(false)
  const { tracker } = useWebTracking()

  const [removePersonFromRole] = useRemoveOutsourcedAccountant({
    onCompleted: () => {
      logInfo('removeOutsourcedAccountant', {
        attributes: {
          creditAppId: appId,
          result: `outsourced accountant removed successfully`,
        },
        eventName: 'removeOutsourcedAccountant',
      })
      showToast(
        TOAST_TYPE_SUCCESS,
        <FormattedMessage
          defaultMessage='Removed outsourced accountant'
          id='sputnik.OutsourcedAccountantSection__24kfwg'
        />,
        {
          autoHideDelay: 5000,
        },
      )
    },
    onError: (error) => {
      logError(error, {
        attributes: {
          creditAppId: appId,
          action: 'removeOutsourcedAccountant',
          message: error?.message,
          result: 'could not remove outsourced accountant',
        },
        eventName: 'removeOutsourcedAccountant',
      })
      showToast(
        TOAST_TYPE_DANGER,
        <FormattedMessage
          defaultMessage='Failed to remove outsourced accountant'
          id='sputnik.OutsourcedAccountantSection__NqMxcs'
        />,
        {
          className: 'fs-unmask',
        },
      )
    },
  })

  const {
    data,
    loading: isLoadingOutsourcedAccountantData,
    error,
  } = useGetPeopleInfoData({
    variables: {
      creditApplicationId: appId ?? '',
    },
  })

  useApolloQueryErrorHandling({
    error,
    toast: {
      message: (
        <FormattedMessage
          defaultMessage='Failed to load outsourced accountant data'
          id='sputnik.OutsourcedAccountantSection__s7yIL9'
        />
      ),
    },
    logging: {
      eventName: 'getOutsourcedAccountant',
      message: 'could not get outsourced accountant data',
      attributes: {
        creditAppId: appId,
      },
    },
  })

  const onDelete = async () => {
    await removePersonFromRole({
      variables: {
        creditApplicationId: appId ?? '',
        outsourcedAccountant: null,
        hasOutsourcedAccountant: false,
      },
    })
  }

  const hasOutsourcedAccountant = !!data?.creditApplication?.outsourcedAccountant
  const { id, firstName, lastName, email } = data?.creditApplication?.outsourcedAccountant ?? {}

  const infoComplete = limitedAddPersonForm.isValidSync({
    firstName,
    lastName,
    email,
  })

  const disableOutsourcedAccountantActions = isMajorAccountChange

  return (
    <PersonSection
      disableAddPersonButton={isLoadingOutsourcedAccountantData || !!id}
      subtitle={
        <FormattedMessage
          defaultMessage="An outsourced accountant is an external accountant that manage's a business's bookkeeping and finances"
          id='sputnik.OutsourcedAccountantSection__eETHGw'
        />
      }
      title={
        <FormattedMessage
          defaultMessage='Outsourced accountant'
          id='sputnik.OutsourcedAccountantSection__h8O5Rm'
        />
      }
      onOpenDrawer={() => {
        tracker?.track('se_signup__add_person__fired', {
          person_type: 'outsourcedAccountant',
        })
        setIsShowingAddOutsourcedAccountantDrawer(true)
      }}
    >
      {hasOutsourcedAccountant && (
        <PersonRecordWithActions
          disableDelete={disableOutsourcedAccountantActions}
          disableEdit={disableOutsourcedAccountantActions}
          editDrawer={UpdateOutsourcedAccountantDrawer}
          firstName={firstName || ''}
          lastName={lastName || ''}
          personId={id || ''}
          subtext={{
            showTag: false,
            infoComplete,
          }}
          trackerId='outsourcedAccountant'
          onDeleteClick={onDelete}
        />
      )}
      <AddOutsourcedAccountantDrawer
        isShowing={isShowingAddOutsourcedAccountantDrawer}
        onClose={() => {
          tracker?.track('se_signup__add_person_drawer_cancelled__fired', {
            person_type: 'outsourcedAccountant',
          })
          setIsShowingAddOutsourcedAccountantDrawer(false)
        }}
      />
    </PersonSection>
  )
}

export default OutsourcedAccountantSection
